import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useStore } from "../context/StoreContext";
import { FiTrash2, FiShoppingBag, FiAlertCircle } from "react-icons/fi";
import { getProductImage } from "../utils/imageHelper";
import "./Cart.css";

const Cart = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, getCartTotal, validateCart, removedItems, clearRemovedItems } = useCart();
  const { storeClosed, storeClosedMessage } = useStore();
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => {
    validateCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemove = (productId, size, productName) => {
    setConfirmAction({
      message: `Êtes-vous sûr de vouloir supprimer "${productName}" de votre panier ?`,
      onConfirm: () => {
        removeFromCart(productId, size);
        setShowConfirm(false);
      }
    });
    setShowConfirm(true);
  };

  const handleQuantityChange = (productId, size, currentQuantity, newQuantity, productName) => {
    if (newQuantity === 0) {
      setConfirmAction({
        message: `Êtes-vous sûr de vouloir supprimer "${productName}" de votre panier ?`,
        onConfirm: () => {
          updateQuantity(productId, size, 0);
          setShowConfirm(false);
        }
      });
      setShowConfirm(true);
    } else {
      updateQuantity(productId, size, newQuantity);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="cart-empty">
        <FiShoppingBag className="empty-icon" />
        <h2>Votre panier est vide</h2>
        <p>Découvrez notre collection et ajoutez des produits</p>
        <button className="btn btn-primary" onClick={() => navigate("/shop")}>
          Voir la boutique
        </button>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="container">
        <h1 className="page-title">Panier</h1>

        {removedItems.length > 0 && (
          <div className="cart-removed-notice">
            <FiAlertCircle className="notice-icon" />
            <div className="notice-content">
              <strong>Produit(s) retiré(s) de votre panier :</strong>
              <ul>
                {removedItems.map((name, i) => (
                  <li key={i}>« {name} » n'est plus disponible à la vente.</li>
                ))}
              </ul>
            </div>
            <button className="notice-close" onClick={clearRemovedItems} aria-label="Fermer">×</button>
          </div>
        )}

        {storeClosed && (
          <div className="cart-store-closed-notice">
            <span className="notice-icon-emoji">🔒</span>
            <span>
              {storeClosedMessage && storeClosedMessage.trim()
                ? storeClosedMessage
                : "La boutique est temporairement fermée. Les achats sont désactivés."}
            </span>
          </div>
        )}

        <div className="cart-layout">
          <div className="cart-items">
            {cart.map((item, index) => (
              <div
                key={`${item.product._id}-${item.size}-${index}`}
                className="cart-item"
              >
                <button
                  className="remove-btn-top"
                  onClick={() => handleRemove(item.product._id, item.size, item.product.name)}
                  title="Supprimer du panier"
                >
                  <FiTrash2 />
                </button>
                <img
                  src={getProductImage(item.product.images?.[0])}
                  alt={item.product.name}
                  className="cart-item-image"
                />
                <div className="cart-item-details">
                  <h3>{item.product.name}</h3>
                  <p className="item-variant">Taille: {item.size}</p>
                  <p className="item-price">{item.product.price}€</p>
                </div>
                <div className="cart-item-actions">
                  <div className="quantity-control">
                    <button
                      onClick={() =>
                        handleQuantityChange(
                          item.product._id,
                          item.size,
                          item.quantity,
                          item.quantity - 1,
                          item.product.name
                        )
                      }
                    >
                      -
                    </button>
                    <span>{item.quantity}</span>
                    <button
                      onClick={() =>
                        updateQuantity(
                          item.product._id,
                          item.size,
                          item.quantity + 1
                        )
                      }
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="cart-item-total">
                  {(item.product.price * item.quantity).toFixed(2)}€
                </div>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <h2>Récapitulatif</h2>
            <div className="summary-line">
              <span>Sous-total</span>
              <span>{getCartTotal().toFixed(2)}€</span>
            </div>
            <div className="summary-total">
              <span>Total</span>
              <span>{getCartTotal().toFixed(2)}€</span>
            </div>
            <button
              className="btn btn-primary checkout-btn"
              onClick={() => navigate("/checkout")}
              disabled={storeClosed}
              title={storeClosed ? "La boutique est temporairement fermée" : undefined}
            >
              Passer la commande
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => navigate("/shop")}
            >
              Continuer mes achats
            </button>
          </div>
        </div>
      </div>

      {showConfirm && confirmAction && (
        <div className="confirm-overlay" onClick={() => setShowConfirm(false)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">
              <FiAlertCircle />
            </div>
            <h3>Confirmation</h3>
            <p>{confirmAction.message}</p>
            <div className="confirm-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowConfirm(false)}
              >
                Annuler
              </button>
              <button
                className="btn btn-danger"
                onClick={confirmAction.onConfirm}
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;
